import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ModalFormHostStateService } from '@shared/components/modal-form-host/modal-form-host-state.service';
import { DefaultLocale, TaskStatusTypes } from '@shared/constants/constants';
import { NoteType } from '@shared/enums/NoteType';
import { DropdownModelString, IDropdownModelString } from '@shared/models/dropdown-string-model';
import { IEventModel } from '@shared/models/event.model';
import { AttendeeNotesFilterModel, IAttendeeNote, IAttendeeNotes } from '@shared/services/api/legacy/attendeenotes-service/attendeenotes-model';
import { InvitedeskApiService } from '@shared/services/api/legacy/invitedesk-api.service';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';
import { IAppState } from '@state/reducers/app/app.reducer';
import { IState } from '@state/state.module';
import { BehaviorSubject, forkJoin as observableForkJoin, Observable, ReplaySubject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { IPermission } from '@shared/services/api/legacy/settings-service/setting-model';

@Component({
    selector: 'app-tasks',
    templateUrl: './tasks.component.html',
    styleUrls: ['./tasks.component.scss']
})
export class TasksComponent implements OnInit {

    public loadingIndicator = false;
    public attendeesNotesList: IAttendeeNote[];

    public eventsForDropdown: IDropdownModelString[] = [];
    public itemsTaskStatusTypes = TaskStatusTypes(this.translation);

    public locale = DefaultLocale;
    public tenantSettings: IPermission;
    private userId: string;

    private state$: Observable<IAppState>;
    private destroy$ = new ReplaySubject<boolean>();

    @Input()
    get attendeesNotes(): IAttendeeNote[] { return this.attendeesNotes$.value; }
    set attendeesNotes(value: IAttendeeNote[]) { this.attendeesNotes$.next(value); }
    public attendeesNotes$: BehaviorSubject<IAttendeeNote[]> = new BehaviorSubject<IAttendeeNote[]>([]);

    @ViewChild(NgForm, { static: true })
    set form(value: NgForm) { this.form$.next(value); }
    get form(): NgForm { return this.form$.value; }
    public form$: BehaviorSubject<NgForm> = new BehaviorSubject<NgForm>(undefined);

    @Output()
    public formSubmitted = new EventEmitter<string[]>();

    constructor(
        private invitedeskApi: InvitedeskApiService,
        private translation: TranslateService,
        private state: ModalFormHostStateService,
        private store: Store<IState>
    ) {}

    public ngOnInit() {
        this.state.form = this.form;
        this.form$.pipe(
            takeUntil(this.destroy$),
            filter(x => !!x),)
            .subscribe(x => this.state.form = x);

        this.state$ = this.store.select(x => x.app);
        this.state$.pipe(
            takeUntil(this.destroy$),
            tap(x => {
                // Set locale
                this.locale = x.profile.localeId;
                this.userId = x.profile.id;
                this.tenantSettings = x.environment.tenantSettings;
            }))
            .subscribe();

        // Build dropdown of events based on attendeesNotes data
        const eventsList: IEventModel[] = [];
        this.attendeesNotes?.forEach(attendeesNote => {
            if (!eventsList?.find(x => x?.id === attendeesNote?.event?.id)) {
                eventsList.push(attendeesNote.event);
            }
        });
        eventsList?.forEach(el => {
            this.eventsForDropdown.push(new DropdownModelString(el?.id, el?.name));
        });

        this.attendeesNotesList = this.attendeesNotes;
    }

    public changeFilter(filterType: string, value: any) {
        const filterTasks = new AttendeeNotesFilterModel();
        filterTasks.noteType = NoteType.Task;
        filterTasks.userId = this.userId;
        switch (filterType) {
            case 'eventId':
                filterTasks.eventId = value;
                break;
            case 'taskStatusType':
                filterTasks.taskStatusType = value;
                break;
        }
        this.getAttendeesNotes(filterTasks);
    }

    public getAttendeesNotes(filterNotes: AttendeeNotesFilterModel = new AttendeeNotesFilterModel()) {
        this.loadingIndicator = true;
        observableForkJoin(
            this.invitedeskApi.attendeeNotes.getAttendeesNotes(filterNotes),
        ).subscribe(
        (
            response: [
                SuccessResponse<IAttendeeNotes>
            ]
        ) => {
                this.attendeesNotesList = response[0].data?.attendeeNotes;
                this.loadingIndicator = false;
            },
            (error: ErrorResponse) => {
                this.loadingIndicator = false;
                console.error('show notification', error.error);
            }
        );
    }

    public onCheckboxChange(event: any, id: string) {
        this.attendeesNotes.find(x => x.id === id).isCompleted = event.target.checked;
    }

    public onSubmit() {
        const completedTasks = [];
        this.attendeesNotes?.forEach(attendeesNote => {
            if (attendeesNote?.isCompleted) {
                completedTasks.push(attendeesNote?.id);
            }
        });
        this.formSubmitted.emit(completedTasks);
    }
}
